/**
 * AdminDashboard.jsx - Admin Dashboard module
 * View metrics, late records, and user activity (Manager/Admin only)
 */
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useUser } from '../contexts/UserContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatNumber, formatDate, getErrorMessage } from '../utils/helpers';

const AdminDashboard = () => {
  const { showError } = useToast();
  const { isManager, isAdmin } = useUser();
  
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [lateRecords, setLateRecords] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [subordinateMetrics, setSubordinateMetrics] = useState([]);

  useEffect(() => {
    if (isManager || isAdmin) {
      loadDashboardData();
    }
  }, [isManager, isAdmin]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [summaryData, lateData, subordinatesData] = await Promise.all([
        adminAPI.getSummary(),
        adminAPI.getLateRecords({ page: 1, page_size: 50 }),
        adminAPI.getSubordinateMetrics(),
      ]);

      setSummary(summaryData);
      setLateRecords(lateData.items || []);
      setSubordinateMetrics(subordinatesData);

      if (isAdmin) {
        const activityData = await adminAPI.getUserActivity();
        setUserActivity(activityData);
      }
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (!isManager && !isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-600">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          View metrics, late records, and user activity
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-600">Total Records</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">
              {formatNumber(summary.TotalRecords)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-600">Reviewed</div>
            <div className="mt-2 text-3xl font-bold text-green-600">
              {formatNumber(summary.TotalReviewed)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-600">Pending</div>
            <div className="mt-2 text-3xl font-bold text-yellow-600">
              {formatNumber(summary.TotalPending)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-sm font-medium text-gray-600">Late</div>
            <div className="mt-2 text-3xl font-bold text-red-600">
              {formatNumber(summary.TotalLate)}
            </div>
          </div>
        </div>
      )}

      {/* Subordinate Metrics (Manager View) */}
      {subordinateMetrics.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Performance</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Total Records</th>
                  <th>Reviewed</th>
                  <th>Pending</th>
                  <th>Late</th>
                  <th>Completion %</th>
                </tr>
              </thead>
              <tbody>
                {subordinateMetrics.map((metric) => (
                  <tr key={metric.EmployeeUserID}>
                    <td>{metric.EmployeeName}</td>
                    <td>{formatNumber(metric.TotalRecords)}</td>
                    <td className="text-green-600">{formatNumber(metric.ReviewedRecords)}</td>
                    <td className="text-yellow-600">{formatNumber(metric.PendingRecords)}</td>
                    <td className="text-red-600">{formatNumber(metric.LateRecords)}</td>
                    <td>
                      {metric.TotalRecords > 0
                        ? `${((metric.ReviewedRecords / metric.TotalRecords) * 100).toFixed(1)}%`
                        : '0%'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Late Records */}
      {lateRecords.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Late Records ({lateRecords.length})
          </h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Plant</th>
                  <th>Material</th>
                  <th>Description</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Days Past Due</th>
                </tr>
              </thead>
              <tbody>
                {lateRecords.map((record, index) => (
                  <tr key={index}>
                    <td>{record.Plant}</td>
                    <td>{record.Material}</td>
                    <td>{record.MaterialDescription}</td>
                    <td>{record.AssignedToPlannerName}</td>
                    <td>{formatDate(record.RecordReviewDueDate)}</td>
                    <td className="text-red-600 font-semibold">{record.DaysPastDue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Activity (Admin only) */}
      {isAdmin && userActivity.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">All User Activity</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Total Records</th>
                  <th>Reviewed</th>
                  <th>Pending</th>
                  <th>Late</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {userActivity.map((activity) => (
                  <tr key={activity.UserID}>
                    <td>{activity.UserName || activity.UserID}</td>
                    <td>{formatNumber(activity.TotalRecords)}</td>
                    <td>{formatNumber(activity.ReviewedRecords)}</td>
                    <td>{formatNumber(activity.PendingRecords)}</td>
                    <td>{formatNumber(activity.LateRecords)}</td>
                    <td>{formatDate(activity.LastActivityDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
